import { Component, ElementRef, Inject, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { FormControl } from '@angular/forms';
import { Observable, Subject, debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs';
import { Group } from '../../../models/group';
import { User } from '../../../models/user';
import { UsersService } from '../../../services/user.service';
import { GroupsService } from '../../../services/groups.service';
import { SlugInputDirective } from '../../../directives/slug-input.directive';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-group-editor',
  standalone: true,
  templateUrl: './group-editor.component.html',
  styleUrls: ['./group-editor.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatSelectModule,
    SlugInputDirective,
    DragDropModule
],
})
export class GroupEditorComponent {
  @ViewChild('userFilterInput', { static: false }) input!: ElementRef<HTMLInputElement>;
  title: string = "Edit Department";
  group!: WritableSignal<Group>;

  tagControl = new FormControl('');
  memberFilter = new FormControl('');
  membershipType = new FormControl('');
  filteredTags$: Observable<string[]>;
  userDataSource = new MatTableDataSource<User>([]);
  allTags: string[] = ['Admin', 'Finance', 'HR', 'IT', 'Root']; // Example tag list
  displayedColumns: string[] = ['name', 'association', 'tools'];
  _filter = new Subject<string>();
  filter = this._filter.asObservable();
  originalGroup: Group;
  ownerControl = new FormControl();
  _options = new Subject<User[]>();
  options = this._options.asObservable();
  selectedUser?: User;
  isAdmin = false;

  constructor(
    public dialogRef: MatDialogRef<GroupEditorComponent>,
    private userService: UsersService, private groupService: GroupsService,
    @Inject(MAT_DIALOG_DATA) public data: { isNew: boolean, group: Group }
  ) {
    this.title = this.data?.isNew ? "Add New Department" : "Edit Department";
    const nonNullGroup = this.data.group ?? <Group><any>{ id: undefined, key: '', name: '', members: [] };
    const group = structuredClone(nonNullGroup);
    this.originalGroup = structuredClone(nonNullGroup);
    this.group = signal<Group>(group);
    this.filteredTags$ = this.tagControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterTags(value || ''))
    );
    this.filter.pipe(
      filter(c => c != null),
      debounceTime(100),
      switchMap(c => this.userService.filterUsers([{ chip: null, value: c.trim() }], 5))
    ).subscribe(resp => this._options.next(resp.users));
    this.ownerControl.valueChanges.pipe(
      filter(c => c != null && typeof c === "string"),
      debounceTime(100),
      switchMap(c => this.userService.filterUsers([{ chip: null, value: c?.trim() }], 5, 0, "fullname", "asc"))
    ).subscribe(resp => this._options.next(resp.users));

    this.userService.getCurrentUser().subscribe(user => {
      const roles = user.userRoles.map(ur => ur.role.roleName.toLowerCase());
      this.isAdmin = roles.includes("admin");
    });
  }

  private _filterTags(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.allTags.filter((tag) =>
      tag.toLowerCase().includes(filterValue)
    );
  }

  saveGroup() {
    this.dialogRef.close({ group: this.group(), isNew: this.data.isNew });
  }

  filterUsers() {
    const filterValue = this.input.nativeElement.value.toLowerCase();
    this._filter.next(filterValue);
  }

  cancel() {
    this.dialogRef.close();
  }

  reset() {
    this.group.set(structuredClone(this.originalGroup));
    console.log(this.group(), this.originalGroup);
  }

  formatUserFn(user: User) {
    return user ? user.hawkId : '';
  }

  selectUser(event: MatAutocompleteSelectedEvent): void {
    console.debug(this.selectUser.name);
    // console.info(this.selectUser.name);
    this.selectedUser = event.option.value as User;
    this.ownerControl.setValue('');
  }

  public displayOwnerWith(user: User) {
    return user?.hawkId;
  }
}
